import { Text, SimpleGrid, Box, Button } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import Divider from 'views/home_form/components/Divider';

import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';

import {
  changeDwellingAttachment,
  addDwellingAttachment,
  removeDwellingAttachment,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface AttachmentsProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const Attachments = ({ dwelling, index }: AttachmentsProps) => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const onAttachmentChange = (
    event: any,
    attachmentIndex: number,
    field: string,
  ) => {
    const { value } = event.target;

    dispatch(
      changeDwellingAttachment({
        dwellingIndex: index,
        attachmentIndex,
        name: field,
        value,
      }),
    );
  };

  const onAttachmentDateChange = (
    date: Date,
    attachmentIndex: number,
    field: string,
  ) => {
    dispatch(
      changeDwellingAttachment({
        dwellingIndex: index,
        attachmentIndex,
        name: field,
        value: getDateString(date),
      }),
    );
  };

  const handleAddAttachment = () => {
    if (!dwelling.attachments || dwelling.attachments.length < 6) {
      dispatch(addDwellingAttachment(index));
    }
  };

  const handleRemoveAttachment = (attachmentIndex: number) => {
    dispatch(
      removeDwellingAttachment({ dwellingIndex: index, attachmentIndex }),
    );
  };

  return (
    <Box mt="20px">
      <Divider mb="15px">Attachments</Divider>

      {dwelling.attachments && dwelling.attachments.length > 0 ? (
        <>
          {dwelling.attachments.map((attachment, attachmentIndex: number) => (
            <Box
              key={attachmentIndex}
              mt={attachmentIndex === 0 ? '0' : '15px'}
              p="15px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="15px"
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Attachment {attachmentIndex + 1}
                </Text>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => handleRemoveAttachment(attachmentIndex)}
                >
                  Remove
                </Button>
              </Box>

              <SimpleGrid columns={2} spacing="15px">
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Description"
                  name={`attachment_description_${attachmentIndex}`}
                  value={attachment.description || ''}
                  onChange={(e: any) =>
                    onAttachmentChange(e, attachmentIndex, 'description')
                  }
                />
                <DateInput
                  section={SECTIONS.DWELLINGS}
                  label="Date Completed"
                  date={getDateFromString(attachment.date_completed)}
                  onChange={(date: Date) =>
                    onAttachmentDateChange(
                      date,
                      attachmentIndex,
                      'date_completed',
                    )
                  }
                />
              </SimpleGrid>
            </Box>
          ))}
        </>
      ) : (
        <Text fontSize="md" color="gray.600" fontStyle="italic">
          No attachments added
        </Text>
      )}

      {(!dwelling.attachments || dwelling.attachments.length < 6) && (
        <Box mt="15px">
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddAttachment}
            disabled={isDisabled}
          >
            Add Attachment ({dwelling.attachments.length || 0}/6)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Attachments;
